import type { ReleaseNotice, UpdateStatus } from '@epc/contracts';
import { useSyncExternalStore } from 'react';

/**
 * Слежение за обновлениями портала (АД-09). Страница раз в минуту (во время выкладки — чаще) спрашивает
 * у API, объявлена ли выкладка, и читает /version.json — версию веб-приложения на сервере. Отсюда плашка:
 * отсчёт до выкладки, «Идёт обновление», «Нет связи с порталом», «Портал обновлён». Страница никогда
 * не перезагружается сама: несохранённое на ней не пропадает.
 */

/** Коммит, из которого собрана открытая страница */
export const BUNDLE_COMMIT: string = typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : 'local';

export interface UpdateState {
  /** Последний ответ API */
  status: UpdateStatus | null;
  /** Разница часов сервера и браузера, мс: отсчёт идёт по серверу */
  offsetMs: number;
  /** Версия веб-приложения на сервере */
  webCommit: string | null;
  /** Запуск веб-сервера, если он поддерживает отметку в version.json. */
  webStartedAt: string | null;
  /** API не отвечает: сеть или выкладка */
  unreachable: boolean;
  /** Сколько опросов подряд не прошло */
  downCount: number;
  /** Коммиты API и веба, когда страница впервые увидела объявленную выкладку */
  baseline: Record<string, { api: string; web: string }>;
  /** Пользователь скрыл «Портал обновлён» для этой версии */
  dismissedWeb: string | null;
  /**
   * Выкладка, которую страница видела объявленной и которая закончилась: сервер уже на новой версии.
   * Даже если страницы не менялись, старая страница может не понять новый сервер — предлагаем обновиться.
   */
  finished: ReleaseNotice | null;
}

let state: UpdateState = {
  status: null,
  offsetMs: 0,
  webCommit: null,
  webStartedAt: null,
  unreachable: false,
  downCount: 0,
  baseline: {},
  dismissedWeb: null,
  finished: null,
};
const listeners = new Set<() => void>();

function set(patch: Partial<UpdateState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

let started = false;
let timer: ReturnType<typeof setTimeout> | undefined;
let inflight: Promise<void> | null = null;
let onRecovered: (() => void) | null = null;

async function fetchStatus(): Promise<UpdateStatus | 'down' | null> {
  try {
    const res = await fetch('/api/v1/updates', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { accept: 'application/json' },
    });
    const body: unknown = await res.json().catch(() => null);
    if (res.ok && body) return body as UpdateStatus;
    // 5xx без ответа API (502 от Caddy, 500 прокси, страница ошибки) — сервер перезапускается или недоступен;
    // ошибка самого API приходит с кодом в теле — значит, он работает
    const apiError = Boolean((body as { error?: { code?: string } } | null)?.error?.code);
    return res.status >= 500 && !apiError ? 'down' : null;
  } catch {
    return 'down';
  }
}

async function fetchWebVersion(): Promise<{ commit: string; startedAt: string | null } | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const v = (await res.json()) as { commit?: unknown; startedAt?: unknown };
    return typeof v.commit === 'string'
      ? { commit: v.commit.slice(0, 7), startedAt: typeof v.startedAt === 'string' ? v.startedAt : null }
      : null;
  } catch {
    return null;
  }
}

async function poll(): Promise<void> {
  const [status, version] = await Promise.all([fetchStatus(), fetchWebVersion()]);
  const web = version?.commit ?? state.webCommit;
  const webStartedAt = version ? version.startedAt : state.webStartedAt;
  // Данные страницы перезапрашиваются только после настоящего перерыва (два опроса подряд без ответа),
  // а не после одного случайного 502
  const wasDown = state.unreachable && state.downCount >= 2;
  if (status === 'down') {
    set({ unreachable: true, downCount: state.downCount + 1, webCommit: web, webStartedAt });
    return;
  }
  const patch: Partial<UpdateState> = { unreachable: false, downCount: 0, webCommit: web, webStartedAt };
  if (status) {
    patch.status = status;
    patch.offsetMs = Date.parse(status.serverTime) - Date.now();
    const rel = status.release;
    if (rel && !state.baseline[rel.id]) {
      patch.baseline = {
        ...state.baseline,
        [rel.id]: { api: status.commit, web: web ?? state.webCommit ?? BUNDLE_COMMIT },
      };
    }
    // Отмена повторного выпуска того же SHA не означает обновление: нужна наблюдаемая смена версии.
    const prev = state.status?.release;
    if (prev && !rel && releaseDone({ ...state, ...patch }, prev, Date.parse(status.serverTime)))
      patch.finished = prev;
  }
  set(patch);
  if (wasDown) onRecovered?.();
}

function schedule() {
  clearTimeout(timer);
  // Чаще — только пока идёт выкладка; предупреждение о работах может висеть часами
  const active = Boolean(state.status?.release);
  let ms = state.unreachable ? 10_000 : active ? 15_000 : 60_000;
  if (typeof document !== 'undefined' && document.hidden) ms = Math.max(ms, 60_000);
  timer = setTimeout(() => void checkNow(), ms);
}

/** Спросить сейчас (кнопка «Повторить», возврат на вкладку, появление сети) */
export function checkNow(): Promise<void> {
  if (!inflight) {
    inflight = poll().finally(() => {
      inflight = null;
      schedule();
    });
  }
  return inflight;
}

/** Запрос к API не прошёл: нет сети или сервер недоступен (502–504) — проверяем чаще */
export function reportServerTrouble() {
  if (!started) return;
  if (!state.unreachable) set({ unreachable: true });
  void checkNow();
}

export function dismissWebUpdate(commit: string) {
  set({ dismissedWeb: commit });
}

export function startUpdateWatch(opts: { onRecovered?: () => void } = {}) {
  if (started || typeof window === 'undefined') return;
  started = true;
  onRecovered = opts.onRecovered ?? null;
  void checkNow();
  window.addEventListener('online', () => void checkNow());
  window.addEventListener('focus', () => void checkNow());
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void checkNow();
  });
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useUpdateState(): UpdateState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}

/**
 * Нужны версия и наблюдаемая смена версии либо запуск сервиса после объявленного времени.
 * Worker-only этим опросом не подтверждается: ждём отмены или истечения предупреждения.
 */
export function releaseDone(s: UpdateState, rel: ReleaseNotice, now: number): boolean {
  if (now < Date.parse(rel.startsAt)) return false;
  const baseline = s.baseline[rel.id];
  if (!rel.services.includes('api') && !rel.services.includes('web')) return false;
  const api = s.status?.commit ?? '';
  const web = s.webCommit ?? BUNDLE_COMMIT;
  const startsAt = Date.parse(rel.startsAt);
  const apiStart = Date.parse(s.status?.apiStartedAt ?? '');
  const apiRestarted = apiStart >= startsAt && apiStart >= Date.parse(rel.deployingAt ?? '');
  const webRestarted = Date.parse(s.webStartedAt ?? '') >= startsAt;
  const apiDone =
    !rel.services.includes('api') ||
    (api === rel.commit && (apiRestarted || (baseline && baseline.api !== rel.commit)));
  const webDone =
    !rel.services.includes('web') ||
    (web === rel.commit && (webRestarted || (baseline && baseline.web !== rel.commit)));
  return Boolean(apiDone && webDone);
}

export type UpdatePhase =
  | { kind: 'none' }
  | { kind: 'unreachable'; duringRelease: boolean }
  | { kind: 'scheduled'; release: ReleaseNotice; startsAt: number }
  /** Объявленное время прошло, новая версия ещё собирается: портал работает, но может ненадолго пропасть */
  | { kind: 'pending'; release: ReleaseNotice }
  /** Новая версия API запускается (отметка мигратора) */
  | { kind: 'updating'; release: ReleaseNotice }
  | { kind: 'updated'; commit: string }
  | { kind: 'maintenance'; startsAt: number; endsAt: number; message: string | null; by: string | null };

/** Что показать сейчас. now — время сервера, мс */
export function phaseOf(s: UpdateState, now: number): UpdatePhase {
  const rel = s.status?.release ?? null;
  const releaseActive = rel ? !releaseDone(s, rel, now) : false;
  if (s.unreachable) return { kind: 'unreachable', duringRelease: Boolean(rel && releaseActive) };
  if (rel && releaseActive) {
    const startsAt = Date.parse(rel.startsAt);
    if (now < startsAt) return { kind: 'scheduled', release: rel, startsAt };
    return rel.deployingAt ? { kind: 'updating', release: rel } : { kind: 'pending', release: rel };
  }
  const web = s.webCommit;
  if (web && web !== BUNDLE_COMMIT && BUNDLE_COMMIT !== 'local' && web !== 'local' && s.dismissedWeb !== web)
    return { kind: 'updated', commit: web };
  // Сервер обновился, страницы — нет: старая страница может не понять новый сервер
  if (s.finished && s.dismissedWeb !== s.finished.commit)
    return { kind: 'updated', commit: s.finished.commit };
  const m = s.status?.maintenance;
  if (m) {
    const endsAt = Date.parse(m.endsAt);
    if (now < endsAt)
      return {
        kind: 'maintenance',
        startsAt: Date.parse(m.startsAt),
        endsAt,
        message: m.message,
        by: m.createdByName,
      };
  }
  return { kind: 'none' };
}
